import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Info, CheckCircle, AlertCircle, Loader2, Cloud, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import TransporterPortalAdmin from '@/components/admin/TransporterPortalAdmin';

// The live server rejects one huge upload (413). Big CSVs go up in ~2 MB parts (same size the backup upload uses), each
// with the header row repeated; a quoted value spanning lines is never cut. Preamble rows above the 'barcode' header are dropped.
const PART_CHARS = 2 * 1024 * 1024 - 128 * 1024;
const MIN_PART_CHARS = 128 * 1024;
export const splitCsv = (text, name, limit = PART_CHARS) => {
  const lines = text.split(/\r?\n/);
  const h = Math.max(0, lines.slice(0, 15).findIndex((l) => /barcode/i.test(l)));
  const parts = [];
  let cur = [];
  let size = 0;
  let open = false;
  for (let i = h + 1; i < lines.length; i++) {
    const l = lines[i];
    if (!open && !l.trim()) continue;
    cur.push(l);
    size += l.length + 1;
    if ((l.split('"').length - 1) % 2) open = !open;
    if (!open && size >= limit) { parts.push(cur); cur = []; size = 0; }
  }
  if (cur.length) parts.push(cur);
  const base = name.replace(/\.csv$/i, '');
  return parts.map((p, i) => new File([`${lines[h]}\n${p.join('\n')}\n`], `${base}.part${i + 1}.csv`, { type: 'text/csv' }));
};

const httpError = (xhr) => {
  const txt = xhr.responseText || '';
  if (xhr.status === 413) return 'File too large for one upload (HTTP 413) — please split it into smaller files';
  if (!txt) return `Upload failed (HTTP ${xhr.status})`;
  try {
    const parsed = JSON.parse(txt);
    const d = parsed.detail;
    const rid = parsed.request_id ? ` [req:${parsed.request_id}]` : '';
    if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).join('.')}: ${x.msg}`).join('; ') + rid;
    if (typeof d === 'string') return d + rid;
    if (d && typeof d === 'object') return JSON.stringify(d) + rid;
    return parsed.message || txt;
  } catch {
    return /<html/i.test(txt) ? `Upload failed (HTTP ${xhr.status})` : txt;
  }
};

const ImportData = () => {
  const navigate = useNavigate();
  const [masterType, setMasterType] = useState('roles');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSyncingDrive, setIsSyncingDrive] = useState(false);
  const [driveStatus, setDriveStatus] = useState(null);
  const [showDriveConfig, setShowDriveConfig] = useState(false);
  const [driveFileIdInput, setDriveFileIdInput] = useState('');
  const [isSavingFileId, setIsSavingFileId] = useState(false);
  
  // Progress tracking state
  const [importProgress, setImportProgress] = useState({
    isImporting: false,
    currentPhase: '', // 'reading', 'validating', 'importing', 'completed'
    totalRecords: 0,
    processedRecords: 0,
    importedCount: 0,
    updatedCount: 0,
    skippedCount: 0,
    errors: [],
    percentage: 0
  });

  const [masterTypes, setMasterTypes] = useState([
    { value: 'roles', label: 'User Roles', requiredColumns: ['User Roles', '(One role per line)'], endpoint: '/api/imports/roles' },
    { value: 'sites', label: 'Sites (Locations)', requiredColumns: ['Sites (Locations)', '(One site per line)'], endpoint: '/api/imports/sites' },
    { value: 'usns', label: 'USN Codes', requiredColumns: ['USN Code', 'Section'], endpoint: '/api/imports/usns' },
    { value: 'staff', label: 'Staff Details', requiredColumns: ['staff_id', 'staff_name', 'designation', 'section', 'dob (YYYY-MM-DD)', 'doj (YYYY-MM-DD)'], optionalColumns: ['gender (Male/Female)', 'worker_type (Regular/Contract)', 'agency (contract labour agency)', 'roll_status (In Roll/Left)', 'left_date (YYYY-MM-DD)', 'phone'], endpoint: '/api/staff/import' },
    { value: 'greige_sort_nos', label: 'Greige Sort Nos', requiredColumns: ['vendor_name', 'sort_no', 'description', 'fg_name', 'size'], endpoint: '/api/sections/greige/import/sort-nos-bulk', useBulk: true },
    { value: 'colors', label: 'Color Master', requiredColumns: ['red_code', 'green_code', 'blue_code', 'color_name'], endpoint: '/api/sections/greige/import/colors' },
    { value: 'damage_types', label: 'Damage Types', requiredColumns: ['damage_name', 'description'], endpoint: '/api/sections/greige/import/damages' },
    { value: 'item_master', label: 'Item Master (Goods Transfer)', requiredColumns: ['barcode', 'design_name', 'shade', 'size', 'description'], optionalColumns: ['wsp', 'mrp', 'category1', 'category2', 'category3', 'category4', 'item_status', 'item_type', 'velocity_class (optional)', 'pricing_channel (fills existing barcodes too)'], endpoint: '/api/imports/item_master' },
    { value: 'wms_projections', label: 'WMS Monthly Projections', requiredColumns: ['sku_code', 'year', 'jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'], endpoint: '/api/wms/projections/import' },
    { value: 'bin_capacity', label: 'Bin Capacity (SKU-wise)', requiredColumns: ['sku_code', 'sfg_bin_capacity', 'fg_bin_capacity'], endpoint: '/api/wms/bin-capacity/import' },
    { value: 'fg_pick_slips', label: 'FG Pick Slips (from Accounting)', requiredColumns: ['PickSlip', 'Barcode', 'PickSlip Quantity'], optionalColumns: ['Created User', 'Created Date', 'Stockpoint', 'Warehouse', 'Customer', 'State', 'Category', 'Agent', 'Order', 'Design Name', 'Description', 'Shade', 'Size', 'DC Quantity', 'Is Urgent?', 'Remarks'], endpoint: '/api/wms/fg/pick-slips/import' },
    { value: 'rates_master', label: 'Rates Master (Label Pricing)', requiredColumns: ['Barcode', 'Pricing Channel (or pricing_type)', 'Design Name', 'MRP'], optionalColumns: ['Optional Barcode', 'Description', 'Shade', 'Size', 'WSP', 'HSNCode', 'Old Or New (only New rows are kept)', 'Effective From', 'Category', 'Section', 'Group', 'Enabled in app', 'Ginesys QR Code', 'Image URL'], endpoint: '/api/imports/rates_master', splitUpload: true },
    { value: 'sticker_master', label: 'Sticker Master (Label Content)', requiredColumns: ['barcode', 'description_1'], optionalColumns: ['description_2', 'description_3', 'description_4', 'description_5', 'description_6', 'description_7'], endpoint: '/api/imports/sticker_master' },
    { value: 'customer_master', label: 'Customer Master', requiredColumns: ['ID', 'Display Name'], optionalColumns: ['Ginesys ID', 'Legal Name', 'Trade Name', 'Group', 'Place', 'GST', 'PAN', 'Address Line 1', 'Address Line 2', 'District', 'State', 'PinCode', 'Old Address', 'Email', 'Pricing channel', 'Pricing Type', '(CSV or Excel — every column is kept; replaces the whole master)'], endpoint: '/api/imports/customer_master/bulk', useBulk: true },
    { value: 'vendor_master', label: 'Vendor Master', requiredColumns: ['vendor_name'], optionalColumns: ['vendor_code', 'vendor_type', 'contact', 'address', 'gst_no'], endpoint: '/api/imports/vendor_master' }
  ]);

  // Fetch dynamic column definitions on mount
  useEffect(() => {
    fetchImportSchema();
  }, []);

  // Fetch Google Drive sync status when customer_master is selected
  useEffect(() => {
    if (masterType === 'customer_master') {
      fetchDriveStatus();
    }
  }, [masterType]);

  const fetchDriveStatus = async () => {
    try {
      const response = await apiClient.get('/imports/customer_master/google-drive/status');
      setDriveStatus(response.data);
    } catch (error) {
      console.log('Could not fetch drive status:', error);
      setDriveStatus(null);
    }
  };

  const handleSaveDriveFileId = async () => {
    const raw = driveFileIdInput.trim();
    if (!raw) {
      toast.error('Paste a Google Drive link or file ID');
      return;
    }
    setIsSavingFileId(true);
    try {
      const res = await apiClient.put('/imports/customer_master/google-drive/file-id', { file_id: raw });
      toast.success(`Drive link updated (file ID: ${res.data.file_id})`);
      setShowDriveConfig(false);
      setDriveFileIdInput('');
      fetchDriveStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update the Drive link');
    } finally {
      setIsSavingFileId(false);
    }
  };

  const handleGoogleDriveSync = async () => {
    setIsSyncingDrive(true);
    setImportProgress(prev => ({ 
      ...prev, 
      isImporting: true, 
      currentPhase: 'syncing',
      percentage: 50,
      message: 'Syncing from Google Drive...'
    }));

    try {
      toast.info('Syncing from Google Drive... Please wait.');
      
      const response = await apiClient.post('/imports/customer_master/google-drive', {}, {
        timeout: 600000 // 10 minute timeout for large files
      });

      if (response.data.success) {
        setImportProgress(prev => ({
          ...prev,
          currentPhase: 'completed',
          percentage: 100,
          processedRecords: response.data.total_records || 0,
          importedCount: response.data.imported || 0,
          skippedCount: response.data.updated || 0,
          totalRecords: response.data.total_records || 0,
          errors: []
        }));
        toast.success(`Sync completed! ${response.data.imported} records imported`);
        fetchDriveStatus();
      } else {
        throw new Error(response.data.detail || 'Sync failed');
      }
    } catch (error) {
      console.error('Google Drive sync error:', error);
      setImportProgress(prev => ({
        ...prev,
        currentPhase: 'error',
        errors: [error.response?.data?.detail || error.message || 'Sync failed']
      }));
      toast.error(error.response?.data?.detail || error.message || 'Sync failed');
    } finally {
      setIsSyncingDrive(false);
    }
  };

  const fetchImportSchema = async () => {
    try {
      const response = await apiClient.get('/imports/schema');
      if (response.data.masters) {
        // Update masterTypes with dynamic columns from backend
        setMasterTypes(prev => prev.map(master => {
          const backendSchema = response.data.masters.find(m => m.value === master.value);
          if (backendSchema) {
            return { ...master, requiredColumns: backendSchema.requiredColumns };
          }
          return master;
        }));
      }
    } catch (error) {
      // If endpoint doesn't exist, use default columns (backwards compatible)
      console.log('Using default import schema');
    }
  };

  const currentMaster = masterTypes.find(m => m.value === masterType);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const xlsxOk = masterType === 'customer_master' && /\.xlsx?$/i.test(file.name);
      if (!file.name.toLowerCase().endsWith('.csv') && !xlsxOk) {
        toast.error(masterType === 'customer_master' ? 'Please select a CSV or Excel file' : 'Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      // Reset progress when new file selected
      resetProgress();
    }
  };

  const resetProgress = () => {
    setImportProgress({
      isImporting: false,
      currentPhase: '',
      totalRecords: 0,
      processedRecords: 0,
      importedCount: 0,
      updatedCount: 0,
      skippedCount: 0,
      errors: [],
      percentage: 0
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setIsUploading(true);
    setImportProgress(prev => ({ ...prev, isImporting: true, currentPhase: 'reading' }));

    try {
      // Phase 1: Read and parse file locally to get total count
      const isXlsx = /\.xlsx?$/i.test(selectedFile.name);
      const fileContent = isXlsx ? '' : await readFileContent(selectedFile);
      const totalRecords = isXlsx ? 0 : Math.max(0, fileContent.split('\n').filter(line => line.trim()).length - 1); // Exclude header

      setImportProgress(prev => ({ 
        ...prev, 
        totalRecords, 
        currentPhase: 'validating',
        percentage: 5
      }));

      const endpoint = currentMaster.endpoint;
      
      // Check if this master type uses bulk (non-streaming) import
      if (currentMaster.useBulk) {
        await handleBulkImport(endpoint, totalRecords);
      } else {
        // All other imports use SSE streaming for real-time progress
        await handleStreamingImport(endpoint, totalRecords, fileContent);
      }

    } catch (error) {
      console.error('Import error:', error);
      setImportProgress(prev => ({ 
        ...prev, 
        currentPhase: 'error',
        errors: [error.response?.data?.detail || error.message || 'Import failed']
      }));
      toast.error(error.response?.data?.detail || error.message || 'Import failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle bulk (non-streaming) import for large files that may timeout with streaming
  const handleBulkImport = async (endpoint, totalRecords) => {
    const formData = new FormData();
    formData.append('file', selectedFile);

    setImportProgress(prev => ({ 
      ...prev, 
      currentPhase: 'importing', 
      percentage: 20,
      totalRecords 
    }));

    toast.info('Importing... This may take a few minutes for large files.');

    try {
      // `endpoint` is defined with a leading `/api`, but apiClient's baseURL
      // already ends in `/api`. Strip it here so the bulk (apiClient) path
      // doesn't produce a doubled `/api/api/...` → 404. (Streaming/other paths
      // use raw XHR with the full path, so they must keep the `/api`.)
      const response = await apiClient.post(endpoint.replace(/^\/api/, ''), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000 // 10 minute timeout
      });

      if (response.data.success) {
        setImportProgress(prev => ({
          ...prev,
          currentPhase: 'completed',
          percentage: 100,
          processedRecords: response.data.total_records || totalRecords,
          totalRecords: response.data.total_records || prev.totalRecords,
          importedCount: response.data.imported || 0,
          updatedCount: response.data.updated || 0,
          skippedCount: response.data.skipped || 0,
          recordsNow: response.data.records_now,
          previousRecords: response.data.previous_records,
          removedCount: response.data.removed,
          errors: response.data.errors || []
        }));
        toast.success(`Import completed! ${response.data.imported || 0} new, ${response.data.updated || 0} updated`);
      } else {
        throw new Error(response.data.detail || 'Import failed');
      }
    } catch (error) {
      console.error('Bulk import error:', error);
      setImportProgress(prev => ({
        ...prev,
        currentPhase: 'error',
        errors: [error.response?.data?.detail || error.message || 'Import failed']
      }));
      toast.error(error.response?.data?.detail || error.message || 'Import failed');
    }
  };

  // One SSE import request (XHR, not fetch: the platform's fetch wrappers eat multipart bodies — 422 "Field required").
  // Resolves with the server's `completed` event; rejects on an error event / HTTP error.
  const streamPart = (endpoint, file, onData) => new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}${endpoint}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('session_token')}`);
    xhr.setRequestHeader('X-Site-Id', localStorage.getItem('active_site_id') || 'salem');
    xhr.responseType = 'text';
    xhr.timeout = 600000;
    let lastReadIndex = 0;
    let buffer = '';
    let done = null;
    let failed = false;
    const parseChunk = (chunk) => {
      buffer += chunk;
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (failed || !line.startsWith('data: ')) continue;
        let data;
        try { data = JSON.parse(line.slice(6)); } catch { continue; }
        if (data.error) { failed = true; reject(new Error(data.error)); try { xhr.abort(); } catch {} return; }
        if (data.phase === 'completed') done = data; else onData(data);
      }
    };
    const readNew = () => {
      if (xhr.responseText && xhr.responseText.length > lastReadIndex) {
        parseChunk(xhr.responseText.substring(lastReadIndex));
        lastReadIndex = xhr.responseText.length;
      }
    };
    xhr.onprogress = readNew;
    xhr.onload = () => {
      if (failed) return;
      readNew();
      if (xhr.status >= 200 && xhr.status < 300) { resolve(done || {}); return; }
      const err = new Error(httpError(xhr));
      err.status = xhr.status;
      reject(err);
    };
    xhr.onerror = () => { if (!failed) reject(new Error('Network error during upload — check connection and try again')); };
    xhr.ontimeout = () => { if (!failed) reject(new Error('Import timed out. Please try with a smaller file or contact support.')); };
    xhr.send(formData);
  });

  const handleStreamingImport = async (endpoint, totalRecords, fileContent) => {
    setImportProgress(prev => ({ ...prev, currentPhase: 'uploading', percentage: 10 }));
    const parts = currentMaster.splitUpload && fileContent ? splitCsv(fileContent, selectedFile.name) : [selectedFile];
    const sum = { processed: 0, imported: 0, updated: 0, skipped: 0, skippedOld: 0, errors: [] };
    let limit = PART_CHARS;
    for (let i = 0; i < parts.length; i++) {
      const tag = parts.length > 1 ? `Part ${i + 1} of ${parts.length}` : '';
      let d;
      try {
        d = await streamPart(endpoint, parts[i], (p) => {
          const processed = sum.processed + (p.processed || 0);
          const pct = parts.length > 1 && totalRecords ? Math.round((processed / totalRecords) * 100) : (p.percentage || 0);
          setImportProgress(prev => ({
            ...prev,
            currentPhase: 'importing',
            percentage: Math.min(99, Math.max(15, pct)),
            processedRecords: processed,
            importedCount: sum.imported + (p.imported || 0),
            updatedCount: sum.updated + (p.updated || 0),
            skippedCount: sum.skipped + (p.skipped || 0),
            message: [tag, p.skipped_old !== undefined ? `${sum.skippedOld + p.skipped_old} Old rows skipped` : p.message].filter(Boolean).join(' · '),
          }));
        });
      } catch (e) {
        if (e.status === 413 && currentMaster.splitUpload && limit > MIN_PART_CHARS) {
          // the server's upload limit is smaller than our part — halve the part size for the rest of the file
          limit = Math.max(MIN_PART_CHARS, Math.floor(limit / 2));
          const rest = parts.splice(i);
          const smaller = [];
          for (const f of rest) smaller.push(...splitCsv(await f.text(), selectedFile.name, limit));
          parts.push(...smaller.map((f, k) => new File([f], `${selectedFile.name.replace(/\.csv$/i, '')}.part${i + k + 1}.csv`, { type: 'text/csv' })));
          i -= 1;
          continue;
        }
        if (!i) throw e;
        throw new Error(`${tag} failed: ${e.message} — ${i === 1 ? 'part 1 was' : `parts 1–${i} were`} saved (${sum.imported} new, ${sum.updated} updated). Fix the file and import it again; saved rows are updated, not duplicated.`);
      }
      sum.processed += d.processed || 0;
      sum.imported += d.imported || 0;
      sum.updated += d.updated || 0;
      sum.skipped += d.skipped || 0;
      sum.skippedOld += d.skipped_old || 0;
      sum.errors.push(...(d.errors || []).map((x) => (tag ? `${tag}: ${x}` : x)));
    }
    setImportProgress(prev => ({
      ...prev,
      currentPhase: 'completed',
      percentage: 100,
      processedRecords: sum.processed || totalRecords,
      importedCount: sum.imported,
      updatedCount: sum.updated,
      skippedCount: sum.skipped,
      message: sum.skippedOld ? `${sum.skippedOld} Old rows skipped` : prev.message,
      errors: sum.errors.slice(0, 20),
    }));
    toast.success(`Imported ${sum.imported} new${sum.updated ? `, ${sum.updated} updated` : ''}${sum.skipped ? ` (${sum.skipped} duplicates skipped)` : ''}${parts.length > 1 ? ` in ${parts.length} parts` : ''}`);
  };

  const handleRegularImport = async (endpoint, totalRecords) => {
    // Phase 2: Upload and process with progress
    const formData = new FormData();
    formData.append('file', selectedFile);
      
    const response = await apiClient.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const uploadPercent = Math.round((progressEvent.loaded * 10) / progressEvent.total);
        setImportProgress(prev => ({ ...prev, percentage: 5 + uploadPercent }));
      }
    });

    // Simulate progress for database writes (since we can't get real-time updates without SSE)
    await simulateProgress(totalRecords, response.data);

    // Phase 3: Complete
    setImportProgress(prev => ({ 
      ...prev, 
      currentPhase: 'completed',
      percentage: 100,
      importedCount: response.data.imported || 0,
      updatedCount: response.data.updated || 0,
      skippedCount: response.data.skipped || 0,
      processedRecords: response.data.total || totalRecords,
      errors: response.data.errors || []
    }));

    toast.success(
      <div>
        <p className="font-semibold">{response.data.message}</p>
        <p className="text-sm mt-1">Import ID: {response.data.import_id}</p>
      </div>,
      { duration: 5000 }
    );
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const simulateProgress = async (totalRecords, responseData) => {
    // Since we already have the final result, simulate the progress animation
    const steps = 20;
    const delay = 50;
    
    for (let i = 0; i <= steps; i++) {
      await new Promise(resolve => setTimeout(resolve, delay));
      const progress = 15 + (i / steps) * 85; // 15% to 100%
      const processed = Math.round((i / steps) * totalRecords);
      
      setImportProgress(prev => ({
        ...prev,
        currentPhase: i < steps ? 'importing' : 'completed',
        percentage: Math.round(progress),
        processedRecords: processed
      }));
    }
  };

  const handleNewImport = () => {
    resetProgress();
    setSelectedFile(null);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const getPhaseLabel = () => {
    switch (importProgress.currentPhase) {
      case 'reading': return 'Reading file...';
      case 'validating': return 'Validating data...';
      case 'importing': return 'Importing to database...';
      case 'completed': return 'Import completed!';
      case 'error': return 'Import failed';
      default: return 'Ready to import';
    }
  };

  const getPhaseColor = () => {
    switch (importProgress.currentPhase) {
      case 'completed': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-[#2D5F4F]';
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/dashboard')}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">Import Data</h1>
            <p className="text-sm text-[#E8DCC8]">Upload CSV files (Customer Master: CSV or Excel) to import master data</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-8">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-6 font-display">Upload File</h2>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Master Type</Label>
                <Select 
                  value={masterType} 
                  onValueChange={(val) => {
                    setMasterType(val);
                    resetProgress();
                  }}
                  disabled={importProgress.isImporting}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="master-type-dropdown">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {masterTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>File</Label>
                <div className="mt-1">
                  <label className={`flex items-center justify-center h-12 px-4 border-2 border-[#E5DFD0] rounded-lg transition-colors ${
                    importProgress.isImporting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-[#2D5F4F]'
                  }`}>
                    <FileText className="w-5 h-5 mr-2 text-[#5A5F6E]" />
                    <span className="text-sm text-[#5A5F6E] truncate max-w-[200px]">
                      {selectedFile ? selectedFile.name : 'Choose File'}
                    </span>
                    <input
                      type="file"
                      accept={masterType === 'customer_master' ? '.csv,.xlsx,.xls' : '.csv'}
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={importProgress.isImporting}
                      data-testid="file-input"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Google Drive Sync - Only for Customer Master */}
            {masterType === 'customer_master' && driveStatus?.has_api_key && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="drive-sync-card">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <Cloud className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Google Drive Sync</p>
                      <p className="text-xs text-blue-700">
                        {driveStatus?.last_sync 
                          ? `Last synced: ${new Date(driveStatus.last_sync.imported_at).toLocaleString()}`
                          : 'Not synced yet'}
                      </p>
                      {driveStatus?.file_id && (
                        <p className="text-[11px] text-blue-600/80 mt-0.5 break-all" data-testid="drive-file-id">
                          File ID: {driveStatus.file_id}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => { setShowDriveConfig((v) => !v); setDriveFileIdInput(''); }}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="drive-change-link-btn"
                    >
                      Change link
                    </Button>
                    <Button
                      onClick={handleGoogleDriveSync}
                      disabled={isSyncingDrive || importProgress.isImporting || !driveStatus?.has_file_id}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="google-drive-sync-btn"
                    >
                      {isSyncingDrive ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Sync from Drive
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {showDriveConfig && (
                  <div className="mt-3 pt-3 border-t border-blue-200 space-y-2" data-testid="drive-config-panel">
                    <Label className="text-blue-900 text-sm">Google Drive link or file ID</Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        value={driveFileIdInput}
                        onChange={(e) => setDriveFileIdInput(e.target.value)}
                        placeholder="https://drive.google.com/file/d/FILE_ID/view — or just the file ID"
                        className="bg-white"
                        data-testid="drive-file-id-input"
                      />
                      <Button
                        onClick={handleSaveDriveFileId}
                        disabled={isSavingFileId || !driveFileIdInput.trim()}
                        className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white whitespace-nowrap"
                        data-testid="drive-file-id-save-btn"
                      >
                        {isSavingFileId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Link'}
                      </Button>
                    </div>
                    <p className="text-[11px] text-blue-700/80">
                      Set the sharing to &ldquo;Anyone with the link &rarr; Viewer&rdquo;, then paste the share link here. The file ID is extracted automatically &mdash; no redeploy needed.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Progress Section */}
            {importProgress.isImporting || importProgress.currentPhase === 'completed' || importProgress.currentPhase === 'error' ? (
              <div className="space-y-4 p-6 bg-[#F5F1E8] rounded-lg border border-[#E5DFD0]">
                {/* Phase Label */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {importProgress.currentPhase === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : importProgress.currentPhase === 'error' ? (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <Loader2 className="w-5 h-5 text-[#2D5F4F] animate-spin" />
                    )}
                    <span className={`font-medium ${getPhaseColor()}`}>
                      {getPhaseLabel()}
                    </span>
                  </div>
                  <span className="text-sm text-[#5A5F6E]">
                    {importProgress.processedRecords} / {importProgress.totalRecords} records
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <Progress 
                    value={importProgress.percentage} 
                    className="h-3"
                    data-testid="import-progress"
                  />
                  <div className="flex justify-between text-xs text-[#5A5F6E]">
                    <span>{importProgress.percentage}% complete</span>
                    {importProgress.currentPhase === 'importing' && (
                      <span data-testid="import-progress-message">{importProgress.message || 'Posting records to database...'}</span>
                    )}
                    {importProgress.currentPhase === 'completed' && importProgress.message && (
                      <span data-testid="import-progress-message">{importProgress.message}</span>
                    )}
                  </div>
                </div>

                {/* Stats */}
                {importProgress.currentPhase === 'completed' && (
                  <div className="grid grid-cols-3 gap-4 pt-2">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{importProgress.importedCount}</p>
                      <p className="text-xs text-green-700">New Records</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{importProgress.updatedCount}</p>
                      <p className="text-xs text-blue-700">Updated</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-600">{importProgress.skippedCount}</p>
                      <p className="text-xs text-gray-700">Skipped</p>
                    </div>
                  </div>
                )}
                {importProgress.currentPhase === 'completed' && importProgress.recordsNow != null && (
                  <p className="text-sm text-gray-700" data-testid="import-records-now">
                    Master now has <b>{importProgress.recordsNow.toLocaleString()}</b> records
                    {importProgress.previousRecords != null && ` (was ${importProgress.previousRecords.toLocaleString()})`}
                    {importProgress.removedCount > 0 && ` · ${importProgress.removedCount.toLocaleString()} customers not in the file removed`}
                  </p>
                )}

                {/* Errors */}
                {importProgress.errors.length > 0 && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800 mb-1">Errors:</p>
                    <ul className="text-xs text-red-700 list-disc list-inside">
                      {importProgress.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {importProgress.errors.length > 5 && (
                        <li>...and {importProgress.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* New Import / Retry */}
                {importProgress.currentPhase === 'completed' && (
                  <Button
                    onClick={handleNewImport}
                    variant="outline"
                    className="w-full mt-2"
                    data-testid="import-new-btn"
                  >
                    Start New Import
                  </Button>
                )}
                {importProgress.currentPhase === 'error' && !isUploading && (
                  <div className="flex gap-2 mt-2">
                    <Button onClick={handleUpload} className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D] text-white" data-testid="import-retry-btn">Retry Import</Button>
                    <Button onClick={handleNewImport} variant="outline" className="flex-1" data-testid="import-new-btn">Start New Import</Button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="w-full h-12 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                data-testid="upload-button"
              >
                <Upload className="w-5 h-5 mr-2" />
                {isUploading ? 'Processing...' : 'Upload & Import'}
              </Button>
            )}
          </div>
        </div>

        {/* Required Columns */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 mt-6">
          <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">Required Columns</h3>
          <div className="flex flex-wrap gap-2">
            {currentMaster?.requiredColumns.map((col, idx) => (
              <span
                key={idx}
                className="px-4 py-2 bg-[#F5F1E8] border border-[#E5DFD0] rounded-lg text-sm text-[#1A1F2E]"
              >
                {col}
              </span>
            ))}
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium mb-2">
                Import Process:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
                <li>File is read and validated locally</li>
                <li>Data is uploaded to the server</li>
                <li>Each record is posted to the database</li>
                <li>Progress bar shows real-time status</li>
                <li>Import is marked complete only after all records are saved</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Transporter Portal & DC Invoice Auto-Sync (admin) */}
        <TransporterPortalAdmin />

        {/* Quick Link to Manage Imports */}
        <div className="mt-6 text-center">
          <Button
            variant="link"
            onClick={() => navigate('/admin/manage-imports')}
            className="text-[#2D5F4F]"
          >
            View Import History →
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ImportData;
